import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import CodeEditor from '../components/CodeEditor';
import DrawingCanvas from '../components/DrawingCanvas';
import Timer from '../components/Timer';
import Leaderboard from '../components/Leaderboard';
import useAntiCheat from '../hooks/useAntiCheat';

const GAME_MODE_INFO = {
  'explain-it': {
    name: 'Vysvětli to',
    icon: '💬',
    creationPrompt: 'Napiš 3-5 řádků C# kódu s jasnou funkcí',
    solutionPrompt: 'Vysvětli, co tento kód dělá',
  },
  'bug-hunt': {
    name: 'Hledání chyb',
    icon: '🐛',
    creationPrompt: 'Napiš C# kód s jednou chybou',
    solutionPrompt: 'Najdi a oprав chybu v tomto kódu',
  },
  'predict-output': {
    name: 'Předpověz výstup',
    icon: '🔮',
    creationPrompt: 'Napiš C# kód, který má jasný výstup',
    solutionPrompt: 'Napiš přesný výstup tohoto kódu',
  },
  'code-golf': {
    name: 'Code Golf',
    icon: '⛳',
    creationPrompt: 'Zadání od učitele',
    solutionPrompt: 'Napiš nejkratší možný C# kód pro tento úkol',
  },
  'verbal-to-code': {
    name: 'Ze slov do kódu',
    icon: '📝',
    creationPrompt: 'Popiš programátorský úkol slovy',
    solutionPrompt: 'Naprogramuj tento úkol v C#',
  },
  'teacher-quiz': {
    name: 'Kvízová otázka',
    icon: '❓',
    solutionPrompt: 'Odpověz na otázku učitele',
  },
  'fill-in-code': {
    name: 'Doplň kód',
    icon: '✏️',
    solutionPrompt: 'Doplň chybějící část kódu',
  },
  'click-the-bug': {
    name: 'Klikni na chybu',
    icon: '🎯',
    solutionPrompt: 'Klikni na řádek, kde je chyba',
  },
  'draw': {
    name: 'Nakresli to',
    icon: '🎨',
    creationPrompt: 'Napiš, co má druhý hráč nakreslit',
    solutionPrompt: 'Nakresli podle zadání',
  },
  'teacher-draw': {
    name: 'Nakresli to',
    icon: '🖌️',
    solutionPrompt: 'Nakresli podle zadání učitele',
  },
  'abcd': {
    name: 'A/B/C/D',
    icon: '🔤',
    solutionPrompt: 'Vyber správnou odpověď',
  },
  'true-false': {
    name: 'Pravda / Nepravda',
    icon: '✅',
    solutionPrompt: 'Je toto tvrzení pravda nebo nepravda?',
  },
  'text-answer': {
    name: 'Textová odpověď',
    icon: '📝',
    solutionPrompt: 'Napiš svoji odpověď',
  },
  'sort-options': {
    name: 'Řazení možností',
    icon: '🔢',
    solutionPrompt: 'Seřaď možnosti správně (použij šipky)',
  },
  'fill-sentence': {
    name: 'Doplnění do věty',
    icon: '🔍',
    solutionPrompt: 'Doplň chybějící slova do textu',
  },
  'draw-guess': {
    name: 'Kresba s hádáním',
    icon: '🎭',
    creationPrompt: 'Napiš, co má druhý hráč nakreslit',
    solutionPrompt: 'Nakresli podle zadání',
    guessPrompt: 'Co bylo původní zadání? Tipuj!',
  },
};

const StudentGame = () => {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const { socket } = useSocket();

  const [gamePhase, setGamePhase] = useState('lobby');
  const [currentRound, setCurrentRound] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [totalTime, setTotalTime] = useState(0);

  // Creation phase
  const [creationCode, setCreationCode] = useState('');
  const [creationDescription, setCreationDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Solution phase
  const [assignment, setAssignment] = useState(null);
  const [solutionCode, setSolutionCode] = useState('');
  const [solutionText, setSolutionText] = useState('');

  // Results
  const [leaderboard, setLeaderboard] = useState([]);
  const [myResult, setMyResult] = useState(null);

  // Solo round
  const [soloAnswer, setSoloAnswer] = useState('');
  const [selectedAbcd, setSelectedAbcd] = useState('');
  const [selectedBugLine, setSelectedBugLine] = useState(null);
  const [soloResults, setSoloResults] = useState(null);
  const [drawingGetImage, setDrawingGetImage] = useState(null);

  const [selectedTrueFalse, setSelectedTrueFalse] = useState(null);
  const [sortOrder, setSortOrder] = useState([]);
  const [fillBlanks, setFillBlanks] = useState([]);
  const [guessText, setGuessText] = useState('');
  const [guessDrawingData, setGuessDrawingData] = useState(null);

  const sessionId = localStorage.getItem('sessionId');
  const playerName = localStorage.getItem('playerName');

  const { startTracking, getAntiCheatData } = useAntiCheat();

  const stateRef = useRef({});
  stateRef.current = {
    gamePhase, currentRound, submitted,
    creationCode, creationDescription,
    solutionCode, solutionText, drawingGetImage,
    soloAnswer, selectedAbcd, selectedTrueFalse,
    selectedBugLine, sortOrder, fillBlanks,
    guessText,
    getAntiCheatData,
  };

  useEffect(() => {
    if (!socket || !sessionId) {
      navigate('/student');
      return;
    }

    // Socket event handlers
    socket.on('round-started', ({ round, roundNumber, phase, timeLimit }) => {
      setCurrentRound(round);
      setGamePhase(phase);
      setTimeRemaining(timeLimit);
      setTotalTime(timeLimit);
      setCreationCode('');
      setCreationDescription('');
      setSolutionCode('');
      setSolutionText('');
      setSubmitted(false);
      setAssignment(null);
    });

    socket.on('assignment-received', ({ assignment, phase, timeLimit }) => {
      setAssignment(assignment);
      setGamePhase(phase);
      setTimeRemaining(timeLimit);
      setTotalTime(timeLimit);
      setSubmitted(false);
      startTracking();
    });

    socket.on('timer-update', ({ timeRemaining }) => {
      setTimeRemaining(timeRemaining);
    });

    socket.on('time-expired', () => {
      const s = stateRef.current;
      if (s.submitted) return;

      const autoSubmit = () => {
        if (s.gamePhase === 'creation') {
          const submission = {
            code: s.creationCode,
            description: s.creationDescription,
            type: s.currentRound?.type,
          };
          socket.emit('student:submit-creation', { roomCode, sessionId, submission }, () => {});
        } else if (s.gamePhase === 'solution') {
          const antiCheatData = s.getAntiCheatData ? s.getAntiCheatData() : null;
          if (s.currentRound?.type === 'draw' || s.currentRound?.type === 'draw-guess') {
            const imageData = s.drawingGetImage ? s.drawingGetImage() : null;
            socket.emit('student:submit-solution', { roomCode, sessionId, solution: { imageData }, antiCheatData }, () => {});
          } else {
            socket.emit('student:submit-solution', { roomCode, sessionId, solution: { code: s.solutionCode, text: s.solutionText }, antiCheatData }, () => {});
          }
        } else if (s.gamePhase === 'solo-answering') {
          const type = s.currentRound?.type;
          let answer = {};
          if (type === 'abcd' || (type === 'teacher-quiz' && s.currentRound?.answerType === 'abcd')) {
            answer = { answer: s.selectedAbcd };
          } else if (type === 'true-false') {
            answer = { answer: s.selectedTrueFalse };
          } else if (type === 'text-answer' || (type === 'teacher-quiz' && s.currentRound?.answerType !== 'abcd')) {
            answer = { answer: s.soloAnswer };
          } else if (type === 'sort-options') {
            answer = { order: s.sortOrder };
          } else if (type === 'fill-sentence') {
            answer = { blanks: s.fillBlanks };
          } else if (type === 'fill-in-code') {
            answer = { answer: s.soloAnswer };
          } else if (type === 'click-the-bug') {
            answer = { selectedLine: s.selectedBugLine };
          } else if (type === 'teacher-draw') {
            const imageData = s.drawingGetImage ? s.drawingGetImage() : null;
            answer = { imageData };
          }
          const antiCheatData = s.getAntiCheatData ? s.getAntiCheatData() : null;
          socket.emit('student:submit-solo-answer', { roomCode, sessionId, answer, antiCheatData }, () => {});
        } else if (s.gamePhase === 'guess-answering') {
          socket.emit('student:submit-guess', { roomCode, sessionId, guessText: s.guessText }, () => {});
        }
      };

      autoSubmit();
      setSubmitted(true);
    });

    socket.on('evaluation-started', () => {
      setGamePhase('evaluation');
    });

    socket.on('solo-round-started', ({ round, roundNumber, phase, timeLimit }) => {
      setCurrentRound(round);
      setGamePhase('solo-answering');
      setTimeRemaining(timeLimit);
      setTotalTime(timeLimit);
      setSubmitted(false);
      setSoloAnswer('');
      setSelectedAbcd('');
      setSelectedBugLine(null);
      setSelectedTrueFalse(null);
      setSoloResults(null);
      setGuessText('');
      startTracking();
      if (round.type === 'sort-options') {
        const items = [...(round.sortItems || [])];
        for (let i = items.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [items[i], items[j]] = [items[j], items[i]];
        }
        setSortOrder(items);
      }
      if (round.type === 'fill-sentence') {
        const blanks = [];
        const regex = /\[\[(.*?)\]\]/g;
        let m;
        while ((m = regex.exec(round.sentenceTemplate || '')) !== null) blanks.push('');
        setFillBlanks(blanks);
      }
    });

    socket.on('guess-assignment-received', ({ imageData, phase, timeLimit }) => {
      setGuessDrawingData(imageData);
      setGuessText('');
      setSubmitted(false);
      setGamePhase('guess-answering');
      setTimeRemaining(timeLimit);
      setTotalTime(timeLimit);
    });

    socket.on('solo-results', ({ results, round }) => {
      setSoloResults(results);
      setGamePhase('solo-results');
    });

    socket.on('grading-result', (result) => {
      if (result.creatorSessionId === sessionId || result.solverSessionId === sessionId) {
        setMyResult(result);
      }
    });

    socket.on('leaderboard-updated', ({ leaderboard }) => {
      setLeaderboard(leaderboard);
      setGamePhase('leaderboard');
    });

    socket.on('next-round-ready', ({ roundNumber }) => {
      setGamePhase('lobby');
    });

    socket.on('game-finished', ({ leaderboard }) => {
      setLeaderboard(leaderboard);
      setGamePhase('finished');
    });

    socket.on('game-ended', () => {
      alert('Hra byla ukončena učitelem');
      navigate('/student');
    });

    return () => {
      socket.off('round-started');
      socket.off('assignment-received');
      socket.off('timer-update');
      socket.off('time-expired');
      socket.off('evaluation-started');
      socket.off('grading-result');
      socket.off('leaderboard-updated');
      socket.off('next-round-ready');
      socket.off('game-finished');
      socket.off('game-ended');
      socket.off('solo-round-started');
      socket.off('solo-results');
      socket.off('guess-assignment-received');
    };
  }, [socket, sessionId, navigate]);

  const handleSubmitGuess = () => {
    if (!guessText.trim()) { alert('Napiš svůj tip'); return; }
    socket.emit(
      'student:submit-guess',
      { roomCode, sessionId, guessText },
      (response) => {
        if (response.success) setSubmitted(true);
      }
    );
  };

  const handleSubmitCreation = () => {
    if (!creationCode.trim() && !creationDescription.trim()) {
      alert('Vyplňte alespoň jedno pole');
      return;
    }

    const submission = {
      code: creationCode,
      description: creationDescription,
      type: currentRound.type,
    };

    socket.emit(
      'student:submit-creation',
      { roomCode, sessionId, submission },
      (response) => {
        if (response.success) {
          setSubmitted(true);
        }
      }
    );
  };

  const handleSubmitSolution = () => {
    const isDrawRound = currentRound?.type === 'draw';

    if (isDrawRound) {
      const imageData = drawingGetImage ? drawingGetImage() : null;
      if (!imageData) {
        alert('Nakresli něco');
        return;
      }
      const solution = { imageData };
      socket.emit(
        'student:submit-solution',
        { roomCode, sessionId, solution, antiCheatData: getAntiCheatData() },
        (response) => {
          if (response.success) setSubmitted(true);
        }
      );
      return;
    }

    if (!solutionCode.trim() && !solutionText.trim()) {
      alert('Vyplňte alespoň jedno pole');
      return;
    }

    const solution = {
      code: solutionCode,
      text: solutionText,
    };

    socket.emit(
      'student:submit-solution',
      { roomCode, sessionId, solution, antiCheatData: getAntiCheatData() },
      (response) => {
        if (response.success) {
          setSubmitted(true);
        }
      }
    );
  };

  const handleSubmitSoloAnswer = () => {
    let answer;
    const type = currentRound.type;
    if (type === 'abcd' || (type === 'teacher-quiz' && currentRound.answerType === 'abcd')) {
      if (!selectedAbcd.trim()) { alert('Vyber možnost'); return; }
      answer = { answer: selectedAbcd };
    } else if (type === 'true-false') {
      if (!selectedTrueFalse) { alert('Vyber pravda nebo nepravda'); return; }
      answer = { answer: selectedTrueFalse };
    } else if (type === 'text-answer' || (type === 'teacher-quiz' && currentRound.answerType !== 'abcd')) {
      if (!soloAnswer.trim()) { alert('Vyplňte odpověď'); return; }
      answer = { answer: soloAnswer };
    } else if (type === 'sort-options') {
      if (sortOrder.some(o => !o)) { alert('Neúplné možnosti'); return; }
      answer = { order: sortOrder };
    } else if (type === 'fill-sentence') {
      if (fillBlanks.some(b => !b.trim())) { alert('Vyplňte všechna pole'); return; }
      answer = { blanks: fillBlanks };
    } else if (type === 'fill-in-code') {
      if (!soloAnswer.trim()) { alert('Vyplňte chybějící část'); return; }
      answer = { answer: soloAnswer };
    } else if (type === 'click-the-bug') {
      if (selectedBugLine === null) { alert('Klikněte na řádek s chybou'); return; }
      answer = { selectedLine: selectedBugLine };
    } else if (type === 'teacher-draw') {
      const imageData = drawingGetImage ? drawingGetImage() : null;
      if (!imageData) { alert('Nakresli něco'); return; }
      answer = { imageData };
    }

    socket.emit(
      'student:submit-solo-answer',
      { roomCode, sessionId, answer, antiCheatData: getAntiCheatData() },
      (response) => {
        if (response.success) {
          setSubmitted(true);
        }
      }
    );
  };

  // Lobby
  if (gamePhase === 'lobby') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Čekárna
            </h1>
            <div className="text-xl text-gray-600 mb-8">
              Ahoj, <strong>{playerName}</strong>!
            </div>
            <div className="bg-primary-50 border-2 border-primary-300 rounded-xl p-8 mb-8">
              <div className="text-sm text-gray-600 mb-2">Kód místnosti</div>
              <div className="text-6xl font-bold text-primary-600 tracking-widest">
                {roomCode}
              </div>
            </div>
            <div className="text-6xl mb-4 animate-pulse-slow">⏳</div>
            <p className="text-xl text-gray-600">
              Čeká se na zahájení učitelem...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Creation Phase
  if (gamePhase === 'creation') {
    const modeInfo = GAME_MODE_INFO[currentRound.type];
    const needsCode = ['explain-it', 'bug-hunt', 'predict-output'].includes(currentRound.type);
    const needsDescription = ['verbal-to-code', 'draw', 'draw-guess'].includes(currentRound.type);

    return (
      <div className="min-h-screen p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{modeInfo.icon}</span>
                  <h1 className="text-3xl font-bold text-gray-800">
                    {modeInfo.name}
                  </h1>
                </div>
                <p className="text-gray-600">{modeInfo.creationPrompt}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Místnost</div>
                <div className="text-2xl font-bold text-primary-600">
                  {roomCode}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Editor/Input */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Tvůj úkol
                </h2>

                {needsCode && (
                  <CodeEditor
                    value={creationCode}
                    onChange={(value) => setCreationCode(value || '')}
                    readOnly={submitted}
                  />
                )}

                {needsDescription && (
                  <textarea
                    value={creationDescription}
                    onChange={(e) => setCreationDescription(e.target.value)}
                    disabled={submitted}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 min-h-[400px] font-mono"
                    placeholder="Popište programátorský úkol..."
                  />
                )}

                {currentRound.taskDescription && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                    <div className="font-bold text-gray-800 mb-2">
                      📋 Pokyn učitele:
                    </div>
                    <div className="text-gray-700">
                      {currentRound.taskDescription}
                    </div>
                  </div>
                )}

                {!submitted ? (
                  <button
                    onClick={handleSubmitCreation}
                    className="mt-4 w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors"
                  >
                    ✅ Odeslat
                  </button>
                ) : (
                  <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-center font-semibold">
                    ✓ Odesláno! Čeká se na další hráče...
                  </div>
                )}
              </div>
            </div>

            {/* Timer & Info */}
            <div className="space-y-6">
              <Timer timeRemaining={timeRemaining} totalTime={totalTime} />
              
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="font-bold text-gray-800 mb-3">
                  {modeInfo.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {modeInfo.creationPrompt}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Solution Phase
  if (gamePhase === 'solution' && assignment) {
    const modeInfo = GAME_MODE_INFO[currentRound.type];
    const needsCode = ['bug-hunt', 'code-golf', 'verbal-to-code'].includes(currentRound.type);
    const needsText = ['explain-it', 'predict-output'].includes(currentRound.type);
    const needsDrawing = ['draw', 'draw-guess'].includes(currentRound.type);

    return (
      <div className="min-h-screen p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{modeInfo.icon}</span>
                  <h1 className="text-3xl font-bold text-gray-800">
                    {modeInfo.name}
                  </h1>
                </div>
                <p className="text-gray-600">{modeInfo.solutionPrompt}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Místnost</div>
                <div className="text-2xl font-bold text-primary-600">
                  {roomCode}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Solution Area */}
            <div className="lg:col-span-2 space-y-6">
              {/* Assignment */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Přiřazený úkol
                </h2>
                {currentRound.taskDescription && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                    <div className="font-bold text-gray-800 mb-2">📋 Pokyn učitele:</div>
                    <div className="text-gray-700">{currentRound.taskDescription}</div>
                  </div>
                )}
                {assignment.code && (
                  <CodeEditor value={assignment.code} readOnly={true} />
                )}
                {assignment.description && (
                  <div className="bg-gray-50 p-4 rounded-lg font-mono text-sm">
                    {assignment.description}
                  </div>
                )}
              </div>

              {/* Your Solution */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Tvoje řešení
                </h2>

                {needsCode && (
                  <CodeEditor
                    value={solutionCode}
                    onChange={(value) => setSolutionCode(value || '')}
                    readOnly={submitted}
                  />
                )}

                {needsText && (
                  <textarea
                    value={solutionText}
                    onChange={(e) => setSolutionText(e.target.value)}
                    disabled={submitted}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 min-h-[200px]"
                    placeholder="Tvoje odpověď..."
                  />
                )}

                {needsDrawing && (
                  <DrawingCanvas
                    onSave={(getImage) => setDrawingGetImage(() => getImage)}
                    disabled={submitted}
                  />
                )}

                {!submitted ? (
                  <button
                    onClick={handleSubmitSolution}
                    className="mt-4 w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors"
                  >
                    ✅ Odeslat řešení
                  </button>
                ) : (
                  <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-center font-semibold">
                    ✓ Odesláno! Čeká se na hodnocení...
                  </div>
                )}
              </div>
            </div>

            {/* Timer & Info */}
            <div className="space-y-6">
              <Timer timeRemaining={timeRemaining} totalTime={totalTime} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Solo Answering Phase — all teacher-set types
  if (gamePhase === 'solo-answering' && currentRound) {
    const modeInfo = GAME_MODE_INFO[currentRound.type] || {};
    const codeLines = (currentRound.buggyCode || '').split('\n');
    const sentenceParts = currentRound.type === 'fill-sentence'
      ? (currentRound.sentenceTemplate || '').split(/\[\[.*?\]\]/)
      : [];
    const sentenceBlanksCount = currentRound.type === 'fill-sentence'
      ? ((currentRound.sentenceTemplate || '').match(/\[\[.*?\]\]/g) || []).length
      : 0;

    return (
      <div className="min-h-screen p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{modeInfo.icon}</span>
                  <h1 className="text-3xl font-bold text-gray-800">{modeInfo.name}</h1>
                </div>
                <p className="text-gray-600">{modeInfo.solutionPrompt}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Místnost</div>
                <div className="text-2xl font-bold text-primary-600">{roomCode}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-6">

                {/* A/B/C/D (type abcd or legacy teacher-quiz abcd) */}
                {(currentRound.type === 'abcd' || (currentRound.type === 'teacher-quiz' && currentRound.answerType === 'abcd')) && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-5">{currentRound.question}</h2>
                    <div className="space-y-3">
                      {(currentRound.options || []).map((option, i) => {
                        const letter = ['A', 'B', 'C', 'D'][i];
                        return (
                          <button key={letter} onClick={() => !submitted && setSelectedAbcd(letter)} disabled={submitted}
                            className={`w-full text-left px-6 py-4 rounded-xl border-2 font-semibold text-lg transition-all ${
                              selectedAbcd === letter ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            } ${submitted ? 'opacity-60 cursor-not-allowed' : ''}`}>
                            <span className="inline-flex w-10 h-10 rounded-full bg-gray-200 items-center justify-center mr-3 font-bold text-sm">{letter}</span>
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Pravda / Nepravda */}
                {currentRound.type === 'true-false' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-5">{currentRound.statement}</h2>
                    <div className="grid grid-cols-2 gap-4">
                      <button onClick={() => !submitted && setSelectedTrueFalse('true')} disabled={submitted}
                        className={`py-6 rounded-xl border-2 text-2xl font-bold transition-all ${
                          selectedTrueFalse === 'true' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-green-300 text-gray-700'
                        } ${submitted ? 'opacity-60 cursor-not-allowed' : ''}`}>
                        ✅ Pravda
                      </button>
                      <button onClick={() => !submitted && setSelectedTrueFalse('false')} disabled={submitted}
                        className={`py-6 rounded-xl border-2 text-2xl font-bold transition-all ${
                          selectedTrueFalse === 'false' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 hover:border-red-300 text-gray-700'
                        } ${submitted ? 'opacity-60 cursor-not-allowed' : ''}`}>
                        ❌ Nepravda
                      </button>
                    </div>
                  </div>
                )}

                {/* Textová odpověď (type text-answer or legacy teacher-quiz text) */}
                {(currentRound.type === 'text-answer' || (currentRound.type === 'teacher-quiz' && currentRound.answerType !== 'abcd')) && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">{currentRound.question}</h2>
                    <textarea value={soloAnswer} onChange={e => setSoloAnswer(e.target.value)} disabled={submitted}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 min-h-[150px]"
                      placeholder="Napiš svoji odpověď..." />
                  </div>
                )}

                {/* Řazení možností */}
                {currentRound.type === 'sort-options' && (
                  <div>
                    {currentRound.question && (
                      <h2 className="text-xl font-bold text-gray-800 mb-4">{currentRound.question}</h2>
                    )}
                    <p className="text-sm text-gray-500 mb-3">Seřaď položky ve správném pořadí pomocí šipek:</p>
                    <div className="space-y-2">
                      {sortOrder.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 bg-white border-2 border-gray-200 rounded-xl px-4 py-3">
                          <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-sm flex-shrink-0">{i + 1}</span>
                          <span className="flex-1 font-medium text-gray-800">{item}</span>
                          <div className="flex flex-col gap-1">
                            <button onClick={() => { if (i === 0 || submitted) return; const o = [...sortOrder]; [o[i-1], o[i]] = [o[i], o[i-1]]; setSortOrder(o); }}
                              disabled={i === 0 || submitted}
                              className="px-2 py-0.5 rounded text-gray-500 hover:text-gray-700 disabled:opacity-30">▲</button>
                            <button onClick={() => { if (i === sortOrder.length-1 || submitted) return; const o = [...sortOrder]; [o[i], o[i+1]] = [o[i+1], o[i]]; setSortOrder(o); }}
                              disabled={i === sortOrder.length-1 || submitted}
                              className="px-2 py-0.5 rounded text-gray-500 hover:text-gray-700 disabled:opacity-30">▼</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Doplnění do věty */}
                {currentRound.type === 'fill-sentence' && (
                  <div>
                    <p className="text-sm text-gray-500 mb-4">Doplň chybějící slova do textu:</p>
                    <div className="text-lg text-gray-800 leading-relaxed bg-gray-50 border border-gray-200 rounded-xl p-4">
                      {sentenceParts.map((part, i) => (
                        <React.Fragment key={i}>
                          <span>{part}</span>
                          {i < sentenceBlanksCount && (
                            <input
                              type="text"
                              value={fillBlanks[i] || ''}
                              onChange={e => { const b = [...fillBlanks]; b[i] = e.target.value; setFillBlanks(b); }}
                              disabled={submitted}
                              className="inline-block mx-1 px-2 py-0.5 border-b-2 border-primary-500 bg-yellow-50 rounded text-primary-700 font-semibold text-base focus:outline-none focus:ring-1 focus:ring-primary-400"
                              style={{ minWidth: '80px', width: `${Math.max(80, (fillBlanks[i] || '').length * 14 + 20)}px` }}
                              placeholder="..."
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fill-in-code (inline input) */}
                {currentRound.type === 'fill-in-code' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Doplň chybějící část kódu</h2>
                    {currentRound.taskDescription && (
                      <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg mb-4">
                        <p className="text-gray-700 text-sm">{currentRound.taskDescription}</p>
                      </div>
                    )}
                    <div className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm font-mono mb-4 overflow-x-auto">
                      {(currentRound.codeTemplate || '').split('___').map((part, i, arr) => (
                        <React.Fragment key={i}>
                          <span className="whitespace-pre">{part}</span>
                          {i < arr.length - 1 && (
                            <input
                              type="text"
                              value={soloAnswer}
                              onChange={e => setSoloAnswer(e.target.value)}
                              disabled={submitted}
                              className="bg-yellow-400 text-black px-1 rounded font-bold font-mono text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
                              style={{ minWidth: '80px', width: `${Math.max(80, soloAnswer.length * 10 + 20)}px` }}
                              placeholder="doplň..."
                              autoFocus
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500">Piš přímo do kódu výše ↑</p>
                  </div>
                )}

                {/* Click-the-bug */}
                {currentRound.type === 'click-the-bug' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Klikni na řádek, kde je chyba</h2>
                    {currentRound.taskDescription && (
                      <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg mb-4">
                        <p className="text-gray-700 text-sm">{currentRound.taskDescription}</p>
                      </div>
                    )}
                    <div className="bg-gray-900 rounded-lg overflow-hidden">
                      {codeLines.map((line, i) => {
                        const lineNum = i + 1;
                        const isSelected = selectedBugLine === lineNum;
                        return (
                          <button key={i} onClick={() => !submitted && setSelectedBugLine(lineNum)} disabled={submitted}
                            className={`w-full text-left flex items-stretch transition-colors ${isSelected ? 'bg-red-600 bg-opacity-40' : 'hover:bg-gray-700'} ${submitted ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                            <span className={`inline-block w-10 text-right pr-3 py-1 text-xs select-none border-r border-gray-700 ${isSelected ? 'text-red-300 font-bold' : 'text-gray-500'}`}>{lineNum}</span>
                            <span className={`flex-1 pl-3 py-1 font-mono text-sm whitespace-pre ${isSelected ? 'text-red-200' : 'text-gray-100'}`}>{line || ' '}</span>
                            {isSelected && <span className="text-red-400 px-3 py-1 text-sm">◀ chyba</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Teacher-draw */}
                {currentRound.type === 'teacher-draw' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Nakresli podle zadání</h2>
                    {currentRound.drawPrompt && (
                      <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg mb-4">
                        <div className="text-sm font-semibold text-purple-700 mb-1">Zadání:</div>
                        <p className="text-gray-800 text-lg">{currentRound.drawPrompt}</p>
                      </div>
                    )}
                    <DrawingCanvas onSave={getImage => setDrawingGetImage(() => getImage)} disabled={submitted} />
                  </div>
                )}

                {!submitted ? (
                  <button onClick={handleSubmitSoloAnswer}
                    className="mt-6 w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors">
                    ✅ Odeslat odpověď
                  </button>
                ) : (
                  <div className="mt-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-center font-semibold">
                    ✓ Odesláno! Čeká se na vyhodnocení...
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <Timer timeRemaining={timeRemaining} totalTime={totalTime} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Draw-Guess: Guess Answering Phase
  if (gamePhase === 'guess-answering') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">🎭</span>
                  <h1 className="text-3xl font-bold text-gray-800">Kresba s hádáním</h1>
                </div>
                <p className="text-gray-600">Co bylo původní zadání? Tipuj!</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Místnost</div>
                <div className="text-2xl font-bold text-primary-600">{roomCode}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Podle čeho hádáš?</h2>
                {guessDrawingData ? (
                  <div className="border-2 border-gray-200 rounded-xl overflow-hidden mb-6 bg-gray-50">
                    <img src={guessDrawingData} alt="Kresba k tipování" className="w-full" />
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded-xl p-8 text-center text-gray-400 mb-6">
                    <div className="text-4xl mb-2">🖼️</div>
                    <p>Kresba se načítá...</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tvůj tip — co bylo zadáno k nakreslení?</label>
                  <input type="text" value={guessText} onChange={e => setGuessText(e.target.value)} disabled={submitted}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-lg"
                    placeholder="Napiš svůj tip..." />
                </div>
                {!submitted ? (
                  <button onClick={handleSubmitGuess}
                    className="mt-4 w-full bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors">
                    ✅ Odeslat tip
                  </button>
                ) : (
                  <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-center font-semibold">
                    ✓ Odesláno! Čeká se na výsledky...
                  </div>
                )}
              </div>
            </div>
            <div>
              <Timer timeRemaining={timeRemaining} totalTime={totalTime} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Solo Results
  if (gamePhase === 'solo-results' && soloResults) {
    const myRes = soloResults.find((r) => r.sessionId === sessionId);
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">
              Výsledky
            </h1>
            {myRes ? (
              <div className={`p-8 rounded-xl mb-6 ${myRes.correct ? 'bg-green-50 border-2 border-green-300' : 'bg-red-50 border-2 border-red-300'}`}>
                <div className="text-6xl mb-4">{myRes.correct ? '✅' : '❌'}</div>
                <div className="text-2xl font-bold mb-2">
                  {myRes.correct ? 'Správně!' : 'Špatně'}
                </div>
                <div className="text-lg text-gray-600">
                  {myRes.correct ? `+${myRes.points} bod${myRes.points === 1 ? '' : 'y'}` : '+0 bodů'}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 p-8 rounded-xl mb-6">
                <div className="text-6xl mb-4">⏰</div>
                <div className="text-xl text-gray-600">Neodevzdáno</div>
              </div>
            )}
            <p className="text-gray-500">Čeká se na další kolo...</p>
          </div>
        </div>
      </div>
    );
  }

  // Evaluation Phase
  if (gamePhase === 'evaluation') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Hodnocení
            </h1>
            <div className="text-6xl mb-4 animate-pulse-slow">👨‍🏫</div>
            <p className="text-xl text-gray-600 mb-8">
              Učitel hodnotí vaše odpovědi...
            </p>

            {myResult && (
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-bold text-lg mb-2">Tvůj výsledek</h3>
                <div className="text-3xl font-bold text-primary-600">
                  {myResult.creatorSessionId === sessionId
                    ? `+${myResult.creatorPoints}`
                    : `+${myResult.solverPoints}`}
                  {' bodů'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Leaderboard
  if (gamePhase === 'leaderboard') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <Leaderboard leaderboard={leaderboard} />
          <div className="mt-8 text-center text-white text-lg">
            Čeká se na další kolo...
          </div>
        </div>
      </div>
    );
  }

  // Finished
  if (gamePhase === 'finished') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              🎉 Hra skončila! 🎉
            </h1>
            <p className="text-xl text-gray-600">
              Výborná práce!
            </p>
          </div>
          <Leaderboard leaderboard={leaderboard} />
          <div className="mt-8 text-center">
            <button
              onClick={() => {
                localStorage.removeItem('sessionId');
                localStorage.removeItem('roomCode');
                localStorage.removeItem('playerName');
                navigate('/student');
              }}
              className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-700"
            >
              Zpět na přihlášení
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-white text-xl">Načítání...</div>
    </div>
  );
};

export default StudentGame;
